import { useEffect, useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  signInWithCustomToken,
  linkWithCredential,
  getRedirectResult,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  OAuthProvider,
  type User,
  type OAuthCredential,
} from 'firebase/auth'
import { auth } from '@/lib/firebase'
import { config } from '@/config/config'
import {
  fetchUser,
  getUserByMobileNumber,
  createSessionCookie,
} from '@/features/Authentication/api'
import {
  authActions,
  selectAuth,
  selectIsAuthenticated,
  type SerializedFirebaseUser,
  type AppUser,
} from '@/features/Authentication/authSlice'
import type { AppDispatch } from '@/App/store'

// ═══════════════════════════════════════════════════════════════
// Public Types
// ═══════════════════════════════════════════════════════════════

export type { AppUser, SerializedFirebaseUser } from '@/features/Authentication/authSlice'
export type { AuthStage } from '@/features/Authentication/authSlice'

export interface OAuthSignInResult {
  user: User
  appUser: AppUser | null
  isNewUser: boolean
}

export interface MobileCheckResult {
  userExists: boolean
  user: AppUser | null
}

// ═══════════════════════════════════════════════════════════════
// Utilities
// ═══════════════════════════════════════════════════════════════

const googleProvider = new GoogleAuthProvider()
const appleProvider = new OAuthProvider('apple.com')

function setSessionCookie(idToken: string): void {
  createSessionCookie(idToken).catch((err) =>
    console.error('Failed to set session cookie:', err)
  )
}

function serializeUser(user: User): SerializedFirebaseUser {
  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    photoURL: user.photoURL,
    phoneNumber: user.phoneNumber,
    providerId: user.providerId,
    emailVerified: user.emailVerified,
  }
}

function extractNameParts(displayName: string | null): {
  firstName: string
  lastName: string
} {
  if (!displayName) return { firstName: '', lastName: '' }
  const parts = displayName.trim().split(' ')
  return {
    firstName: parts[0] || '',
    lastName: parts.slice(1).join(' '),
  }
}

function extractOAuthCredential(
  provider: GoogleAuthProvider | OAuthProvider,
  result: import('firebase/auth').UserCredential
): OAuthCredential | null {
  if (provider instanceof GoogleAuthProvider) {
    return GoogleAuthProvider.credentialFromResult(result)
  }
  return OAuthProvider.credentialFromResult(result)
}

function detectWebView(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent || ''
  return /FBAN|FBAV|Instagram|Twitter|Line|wv|WebView/i.test(ua)
}

function isMobile(): boolean {
  if (typeof navigator === 'undefined') return false
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  )
}

/**
 * Check if redirect flow will work based on hosting setup.
 * Redirect only works reliably on:
 * 1. Firebase Hosting with firebaseapp.com domain
 * 2. Custom domains configured as authDomain in Firebase
 * 
 * For other hosting (Vercel, Netlify, etc.), redirect will fail due to
 * third-party cookie blocking in modern browsers.
 */
function canUseRedirect(): boolean {
  if (typeof window === 'undefined') return false
  
  const hostname = window.location.hostname
  const authDomain = auth.config.authDomain
  
  console.log('[useFirebaseAuth] Checking redirect support:', { hostname, authDomain })
  
  // Localhost always works for testing
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return true
  }
  
  // Firebase Hosting domains work
  if (hostname.endsWith('.firebaseapp.com') || hostname.endsWith('.web.app')) {
    return true
  }
  
  // CRITICAL: Only allow redirect if authDomain matches current hostname
  // This is the ONLY way redirect works with third-party cookie blocking
  // If they don't match, Firebase can't set the auth cookie and redirect fails
  if (authDomain && hostname === authDomain) {
    console.log('[useFirebaseAuth] ✅ Redirect supported - hostname matches authDomain')
    return true
  }
  
  console.log('[useFirebaseAuth] ❌ Redirect NOT supported - hostname does not match authDomain')
  console.log('[useFirebaseAuth] To enable redirect: Set VITE_FIREBASE_AUTH_DOMAIN=' + hostname)
  
  return false
}

// ═══════════════════════════════════════════════════════════════
// Hook
// ═══════════════════════════════════════════════════════════════

export function useFirebaseAuth() {
  const dispatch = useDispatch<AppDispatch>()
  const authState = useSelector(selectAuth)
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const { restaurantId } = config.store

  // Raw Firebase User kept in a ref (not serializable -> not in Redux).
  // Used for getIdToken() refresh calls.
  const firebaseUserRef = useRef<User | null>(null)

  // OAuthCredential kept in a ref (not serializable).
  const credentialRef = useRef<OAuthCredential | null>(null)

  // Prevents the onAuthStateChanged listener from running during
  // an active OAuth popup to avoid duplicate fetchUser calls.
  const skipAuthListenerRef = useRef(false)

  // ─── Backend Sync ──────────────────────────────────────────

  const fetchAppUser = useCallback(
    async (token: string): Promise<AppUser | null> => {
      try {
        const response = await fetchUser({ restaurantId }, token)
        if (response?.code === 200 && response?.data?.user) {
          return response.data.user as AppUser
        }
        return response?.data?.user ?? null
      } catch {
        return null
      }
    },
    [restaurantId]
  )

  // ─── Auth State Listener + Redirect Result Handling ────────

  useEffect(() => {
    let redirectHandled = false
    const wasRedirectPending = sessionStorage.getItem('oauth_redirect_pending') === 'true'

    const redirectPromise = getRedirectResult(auth)
      .then(async (result) => {
        console.log('[useFirebaseAuth] Redirect result:', result ? 'User found' : 'No result', 'Was pending:', wasRedirectPending)
        
        if (!result) {
          // No redirect result
          if (wasRedirectPending) {
            // User was redirected but came back with no result
            // This means third-party cookies are blocked and redirect won't work
            console.error('[useFirebaseAuth] Redirect failed - third-party cookies likely blocked')
            console.error('[useFirebaseAuth] Current domain:', window.location.hostname)
            console.error('[useFirebaseAuth] Auth domain:', auth.config.authDomain)
            console.error('[useFirebaseAuth] See MOBILE_AUTH_SETUP.md for configuration instructions')
            
            sessionStorage.removeItem('oauth_redirect_pending')
            dispatch(authActions.setError(
              'Sign-in redirect failed. Your domain needs to be configured in Firebase. Using popup instead.'
            ))
          }
          return
        }

        // We have a redirect result - user just completed OAuth
        redirectHandled = true
        firebaseUserRef.current = result.user
        console.log('[useFirebaseAuth] Processing redirect result for user:', result.user.uid)

        const token = await result.user.getIdToken()
        credentialRef.current =
          GoogleAuthProvider.credentialFromResult(result) ??
          OAuthProvider.credentialFromResult(result) ??
          null

        const appUser = await fetchAppUser(token)
        console.log('[useFirebaseAuth] App user fetched:', appUser ? 'Existing user' : 'New user')

        if (appUser) {
          setSessionCookie(token)
          dispatch(
            authActions.oauthExistingUser({
              user: serializeUser(result.user),
              appUser,
              idToken: token,
            })
          )
        } else {
          const { firstName, lastName } = extractNameParts(
            result.user.displayName
          )
          dispatch(
            authActions.oauthNewUser({
              user: serializeUser(result.user),
              idToken: token,
              firstName,
              lastName,
            })
          )
        }
      })
      .catch((err) => {
        // Clear loading state and handle redirect errors
        console.error('[useFirebaseAuth] OAuth redirect error:', err)
        sessionStorage.removeItem('oauth_redirect_pending')
        dispatch(authActions.setLoading(false))
        const message = err instanceof Error ? err.message : 'Sign-in failed'
        dispatch(authActions.setError(message))
      })

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (skipAuthListenerRef.current) {
        console.log('[useFirebaseAuth] Skipping auth listener (skipAuthListenerRef is true)')
        return
      }

      await redirectPromise
      if (redirectHandled) {
        console.log('[useFirebaseAuth] Skipping auth listener (redirect already handled)')
        return
      }

      console.log('[useFirebaseAuth] Auth state changed:', user ? `User ${user.uid}` : 'No user', 'Was pending:', wasRedirectPending)

      if (user) {
        firebaseUserRef.current = user
        const token = await user.getIdToken()
        const appUser = await fetchAppUser(token)
        if (appUser) {
          setSessionCookie(token)
        }
        
        // If this was from a redirect, clear the pending flag
        if (wasRedirectPending) {
          console.log('[useFirebaseAuth] Clearing redirect pending flag after auth state change')
          sessionStorage.removeItem('oauth_redirect_pending')
        }
        
        dispatch(
          authActions.initComplete({
            user: serializeUser(user),
            appUser,
            idToken: token,
          })
        )
      } else {
        firebaseUserRef.current = null
        
        // If we were expecting a redirect but got no user, clear the flag
        if (wasRedirectPending) {
          console.log('[useFirebaseAuth] Clearing redirect pending flag - no user found')
          sessionStorage.removeItem('oauth_redirect_pending')
        }
        
        dispatch(
          authActions.initComplete({
            user: null,
            appUser: null,
            idToken: null,
          })
        )
      }
    })

    return () => unsubscribe()
  }, [dispatch, fetchAppUser])

  // ─── OAuth Sign-In (Google / Apple) ────────────────────────

  const handleOAuthSignIn = useCallback(
    async (
      provider: GoogleAuthProvider | OAuthProvider
    ): Promise<OAuthSignInResult | undefined> => {
      const mobile = isMobile()
      const webview = detectWebView()
      const redirectSupported = canUseRedirect()
      
      console.log('[useFirebaseAuth] OAuth Config:', { 
        mobile, 
        webview, 
        redirectSupported,
        hostname: window.location.hostname 
      })
      
      dispatch(authActions.oauthStart())

      // Use redirect for mobile if supported (better UX - no popup)
      if (mobile && !webview && redirectSupported) {
        console.log('[useFirebaseAuth] Using redirect flow (mobile + redirect supported)')
        sessionStorage.setItem('oauth_redirect_pending', 'true')
        try {
          await signInWithRedirect(auth, provider)
          return undefined
        } catch (err) {
          console.error('[useFirebaseAuth] Redirect failed:', err)
          sessionStorage.removeItem('oauth_redirect_pending')
          dispatch(authActions.setLoading(false))
          const message = err instanceof Error ? err.message : 'Sign-in failed'
          dispatch(authActions.setError(message))
          throw err
        }
      }

      // Use popup for desktop, webviews, or when redirect isn't supported
      if (mobile && !redirectSupported) {
        console.warn('[useFirebaseAuth] 📱 Mobile detected but redirect not supported')
        console.warn('[useFirebaseAuth] 💡 To enable redirect (better mobile UX):')
        console.warn('[useFirebaseAuth] 1. Set VITE_FIREBASE_AUTH_DOMAIN=' + window.location.hostname)
        console.warn('[useFirebaseAuth] 2. Add domain to Firebase Console → Auth → Authorized domains')
        console.warn('[useFirebaseAuth] 3. Add redirect URI to OAuth provider')
        console.warn('[useFirebaseAuth] See MOBILE_AUTH_SETUP.md for details')
      }
      console.log('[useFirebaseAuth] Using popup flow')
      skipAuthListenerRef.current = true

      try {
        const result = await signInWithPopup(auth, provider)
        firebaseUserRef.current = result.user
        console.log('[useFirebaseAuth] Popup sign-in successful, user:', result.user.uid)

        const token = await result.user.getIdToken()
        const credential = extractOAuthCredential(provider, result)
        credentialRef.current = credential

        const appUser = await fetchAppUser(token)

        if (appUser) {
          console.log('[useFirebaseAuth] Existing user found')
          setSessionCookie(token)
          dispatch(
            authActions.oauthExistingUser({
              user: serializeUser(result.user),
              appUser,
              idToken: token,
            })
          )
          return { user: result.user, appUser, isNewUser: false }
        }

        console.log('[useFirebaseAuth] New user, needs phone')
        const { firstName, lastName } = extractNameParts(
          result.user.displayName
        )
        dispatch(
          authActions.oauthNewUser({
            user: serializeUser(result.user),
            idToken: token,
            firstName,
            lastName,
          })
        )

        return { user: result.user, appUser: null, isNewUser: true }
      } catch (err: any) {
        console.error('[useFirebaseAuth] Popup sign-in failed:', err)
        const message = err instanceof Error ? err.message : 'Sign-in failed'
        dispatch(authActions.setError(message))
        throw err
      } finally {
        skipAuthListenerRef.current = false
      }
    },
    [dispatch, fetchAppUser]
  )

  const signInWithGoogle = useCallback(
    () => handleOAuthSignIn(googleProvider),
    [handleOAuthSignIn]
  )

  const signInWithApple = useCallback(
    () => handleOAuthSignIn(appleProvider),
    [handleOAuthSignIn]
  )

  // ─── Phone Auth Helpers ────────────────────────────────────

  const checkMobileUser = useCallback(
    async (mobileNumber: string): Promise<MobileCheckResult> => {
      dispatch(authActions.setLoading(true))
      try {
        const response = await getUserByMobileNumber({
          mobileNumber,
          restaurantId,
        })
        dispatch(authActions.setLoading(false))
        const user = response?.user ?? null
        return {
          userExists: response?.success === true && !!user,
          user,
        }
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : 'Failed to check mobile number'
        dispatch(authActions.setError(message))
        throw err
      }
    },
    [dispatch, restaurantId]
  )

  // ─── Post-Auth (for phone flow integration) ────────────────
  // Call after phone OTP verification to set the app user from
  // outside the hook (e.g. from LoginPage).

  const setAppUser = useCallback(
    (user: AppUser) => {
      dispatch(authActions.setAppUser(user))
    },
    [dispatch]
  )

  const completeMobileAuth = useCallback(
    async (
      appUser: AppUser,
      customToken: string,
      linkProvider?: boolean
    ): Promise<void> => {
      skipAuthListenerRef.current = true

      try {
        const result = await signInWithCustomToken(auth, customToken)
        firebaseUserRef.current = result.user

        if (linkProvider && credentialRef.current) {
          await linkWithCredential(result.user, credentialRef.current)
          credentialRef.current = null
        }

        const idToken = await result.user.getIdToken()
        setSessionCookie(idToken)

        dispatch(
          authActions.mobileAuthComplete({
            appUser,
            idToken,
          })
        )
      } catch (err) {
        const code = (err as { code?: string })?.code
        if (code === 'auth/provider-already-linked') {
          await firebaseSignOut(auth)
          firebaseUserRef.current = null
          credentialRef.current = null
          dispatch(authActions.signedOut())
          throw new Error(
            'This phone number is already linked to your email account. Please sign in with that account instead.'
          )
        }
        const message =
          err instanceof Error ? err.message : 'Sign-in failed'
        dispatch(authActions.setError(message))
        throw err
      } finally {
        skipAuthListenerRef.current = false
      }
    },
    [dispatch]
  )

  // ─── Sign Out ──────────────────────────────────────────────

  const signOut = useCallback(
    async (onComplete?: () => void) => {
      await firebaseSignOut(auth)
      firebaseUserRef.current = null
      credentialRef.current = null
      dispatch(authActions.signedOut())
      onComplete?.()
    },
    [dispatch]
  )

  // ─── Token Refresh ─────────────────────────────────────────

  const refreshToken = useCallback(async (): Promise<string | null> => {
    const user = firebaseUserRef.current
    if (!user) return null
    return user.getIdToken(true)
  }, [])

  // ─── Error Handling ────────────────────────────────────────

  const clearError = useCallback(() => {
    dispatch(authActions.clearError())
  }, [dispatch])

  // ─── Public API ────────────────────────────────────────────

  return {
    // State (from Redux)
    user: authState.user,
    appUser: authState.appUser,
    idToken: authState.idToken,
    loading: authState.loading,
    initializing: authState.initializing,
    error: authState.error,
    stage: authState.stage,
    firstName: authState.firstName,
    lastName: authState.lastName,

    // Refs (non-serializable, not in Redux)
    firebaseUser: firebaseUserRef,
    credential: credentialRef,

    // Computed
    isAuthenticated,
    isOAuthAvailable: !detectWebView(),
    isWebView: detectWebView(),

    // OAuth
    signInWithGoogle,
    signInWithApple,

    // Phone helpers
    checkMobileUser,

    // Post-auth
    setAppUser,
    completeMobileAuth,

    // General
    signOut,
    refreshToken,
    clearError,
  }
}
